import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './NewDashboard.css';
import './AdminDashboard.css';
import ActiveRegionalBadge from './common/ActiveRegionalBadge';
import UserAccountMenu from './common/UserAccountMenu';
import { useAuth } from '../contexts/AuthContext';
import RegionalService from '../services/RegionalService';
import areaService from '../services/AreaService';
import materiaService from '../services/MateriaService';
import AsistenteService from '../services/AsistenteService';
import tipoAsistenteService from '../services/TipoAsistenteService';
import {
  isGptClassicEnabled as hasGptClassicFeature,
  isOpenAIAssistantEnabled as hasOpenAIAssistantFeature,
  isRagFlowAssistantEnabled as hasRagFlowAssistantFeature
} from '../utils/adminMenu';
import useRegionalFeatureFlags from '../hooks/useRegionalFeatureFlags';
import AdminRoleMenuOptions from './sidebar/AdminRoleMenuOptions';
import { navigateAdminMenuItem } from './sidebar/menuConfig';
import { findAssistantTypeById, isInstitutionalAssistantType, INSTITUCIONAL_ASSISTANT_TYPE_ID } from '../utils/asistenteTypes';
import {
  buildRagFlowModelLabel,
  getDefaultRagFlowModelId,
  normalizeRagFlowModelsResponse
} from '../utils/ragFlowModels';

const INSTITUCIONAL_ID = INSTITUCIONAL_ASSISTANT_TYPE_ID;
const PROMPT_FEATURES = [
  'Usa exclusivamente los PDFs adjuntos (RAG obligatorio).',
  'Ejecuta file_search antes de responder consultas académicas.',
  'Si no hay evidencia en los PDFs, responde con el mensaje de falta de información.',
  'Respuestas académicas en HTML con citas de PDF y número de página.',
  'Puede responder saludos o uso del asistente sin citar PDFs.',
  'Imágenes o esquemas solo si existen en PDFs o son representaciones fieles con aviso.',
  'Puede armar planes de estudio y actuar como tutor orientado a ayudar al alumno.'
];

const AdminCreateAsistente = ({ provider = 'openai' }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isRagFlowProvider = String(provider).toLowerCase() === 'ragflow';
  const providerLabel = isRagFlowProvider ? 'AVE' : 'OpenAI';
  const adminAssistantsTab = isRagFlowProvider ? 'asistentes-ragflow' : 'asistentes';
  const activeRegional = useMemo(() => RegionalService.getActiveRegional(), []);
  const activeRegionalId = useMemo(() => RegionalService.getActiveRegionalId(), []);
  const activeRegionalName = activeRegional?.nombre || activeRegional?.name || activeRegional?.descripcion || 'Regional seleccionado';
  const { featureFlags } = useRegionalFeatureFlags(user, { enabled: Boolean(user) });
  const isGptClassicEnabled = useMemo(() => hasGptClassicFeature(featureFlags), [featureFlags]);
  const isOpenAIAssistantEnabled = useMemo(() => hasOpenAIAssistantFeature(featureFlags), [featureFlags]);
  const isRagFlowAssistantEnabled = useMemo(() => hasRagFlowAssistantFeature(featureFlags), [featureFlags]);
  const handleAdminMenuSelect = (item) => navigateAdminMenuItem(navigate, item.id);
  const canSelectTipo = true;

  const [form, setForm] = useState({
    nombre: '',
    nicknameUrl: '',
    idTipoAsistente: INSTITUCIONAL_ID,
    descripcion: '',
    promptExtra: '',
    instruccionesInstitucionales: '',
    ragFlowModelo: '',
    idRegional: activeRegionalId || '',
    idArea: '',
    idMateria: '',
    publico: true,
    passwordAsistente: ''
  });
  const [areas, setAreas] = useState([]);
  const [materias, setMaterias] = useState([]);
  const [tiposAsistente, setTiposAsistente] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [archivosPdf, setArchivosPdf] = useState([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [ragFlowModels, setRagFlowModels] = useState([]);
  const [ragFlowModelsLoading, setRagFlowModelsLoading] = useState(false);
  const [ragFlowModelsError, setRagFlowModelsError] = useState('');
  const selectedTipoAsistente = useMemo(
    () => findAssistantTypeById(tiposAsistente, form.idTipoAsistente || INSTITUCIONAL_ID),
    [tiposAsistente, form.idTipoAsistente]
  );
  const materiaEsObligatoria = useMemo(
    () => !isInstitutionalAssistantType(selectedTipoAsistente || { id: form.idTipoAsistente || INSTITUCIONAL_ID }),
    [selectedTipoAsistente, form.idTipoAsistente]
  );
  const shouldShowPromptSettings = !isRagFlowProvider;
  const shouldShowExtraInstructions = shouldShowPromptSettings || isRagFlowProvider;
  const shouldRequireInstitutionalInstructions = shouldShowPromptSettings && !materiaEsObligatoria;
  const selectedRagFlowModelConfig = useMemo(() => {
    if (!form.ragFlowModelo) {
      return null;
    }

    return ragFlowModels.find((model) => model.id === form.ragFlowModelo) || null;
  }, [form.ragFlowModelo, ragFlowModels]);
  const isSubmitDisabled = loading ||
    archivosPdf.length === 0 ||
    (isRagFlowProvider && (ragFlowModelsLoading || !form.ragFlowModelo));

  useEffect(() => {
    const fetchAreasMaterias = async () => {
      try {
        const tasks = [
          areaService.getAreasActivas(activeRegionalId),
          materiaService.getMateriasActivas()
        ];
        if (canSelectTipo) {
          tasks.push(tipoAsistenteService.getTiposActivos());
        }
        const [areasList, materiasList, tiposList] = await Promise.all(tasks);
        const filteredMaterias = (materiasList || []).filter((m) => (m.idRegional || m.IdRegional) === activeRegionalId);
        setAreas(Array.isArray(areasList) ? areasList : []);
        setMaterias(Array.isArray(filteredMaterias) ? filteredMaterias : []);
        if (canSelectTipo) {
          setTiposAsistente(Array.isArray(tiposList) ? tiposList : []);
        }
      } catch (error) {
        setMessage(error.message || 'No pudimos cargar áreas o materias.');
        setMessageType('error');
      }
    };
    if (activeRegionalId) {
      fetchAreasMaterias();
    }
  }, [activeRegionalId, canSelectTipo]);

  useEffect(() => {
    if (!isRagFlowProvider || !activeRegionalId) {
      return;
    }

    let cancelled = false;
    const fetchRagFlowModels = async () => {
      setRagFlowModelsLoading(true);
      setRagFlowModelsError('');
      try {
        const response = await AsistenteService.getRagFlowModelos(activeRegionalId);
        if (cancelled) {
          return;
        }

        const models = normalizeRagFlowModelsResponse(response);
        setRagFlowModels(models);
        const defaultModelId = getDefaultRagFlowModelId(models);
        setForm((prev) => {
          if (!defaultModelId) {
            return prev;
          }

          const currentExists = models.some((model) => model.id === prev.ragFlowModelo);
          return currentExists ? prev : { ...prev, ragFlowModelo: defaultModelId };
        });
      } catch (error) {
        if (!cancelled) {
          setRagFlowModels([]);
          setRagFlowModelsError(error.message || 'No pudimos cargar los modelos de RagFlow.');
        }
      } finally {
        if (!cancelled) {
          setRagFlowModelsLoading(false);
        }
      }
    };

    fetchRagFlowModels();

    return () => {
      cancelled = true;
    };
  }, [isRagFlowProvider, activeRegionalId]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleNicknameChange = (value) => {
    const normalized = value
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '')
      .replace(/-+/g, '-');
    handleChange('nicknameUrl', normalized);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nombre.trim()) {
      setMessage('Completa el nombre del asistente.');
      setMessageType('error');
      return;
    }
    if (!form.idArea) {
      setMessage('Selecciona un área.');
      setMessageType('error');
      return;
    }
    if (materiaEsObligatoria && !form.idMateria) {
      setMessage('Selecciona una materia para asistentes de grado o electiva.');
      setMessageType('error');
      return;
    }
    if (shouldRequireInstitutionalInstructions && !form.instruccionesInstitucionales.trim()) {
      setMessage('Completa las instrucciones del asistente institucional.');
      setMessageType('error');
      return;
    }
    if (isRagFlowProvider && !form.ragFlowModelo.trim()) {
      setMessage('Selecciona un modelo disponible en RagFlow para crear el asistente AVE.');
      setMessageType('error');
      return;
    }
    if (archivosPdf.length === 0) {
      setMessage('Sube al menos un PDF paral asistente.');
      setMessageType('error');
      return;
    }
    if (!form.publico && !form.passwordAsistente.trim()) {
      setMessage('Completa el password del asistente.');
      setMessageType('error');
      return;
    }

    setLoading(true);
    setMessage('Creando asistente...');
    setMessageType('info');
    try {
      const tipoId = canSelectTipo ? (form.idTipoAsistente || INSTITUCIONAL_ID) : INSTITUCIONAL_ID;
      const fd = new FormData();
      fd.append('nombre', form.nombre);
      fd.append('descripcion', form.descripcion || '');
      if (shouldShowExtraInstructions && form.promptExtra?.trim()) {
        fd.append('promptExtra', form.promptExtra.trim());
      }
      if (shouldShowPromptSettings && !materiaEsObligatoria && form.instruccionesInstitucionales.trim()) {
        fd.append('instruccionesInstitucionales', form.instruccionesInstitucionales.trim());
      }
      if (isRagFlowProvider && form.ragFlowModelo.trim()) {
        fd.append('ragFlowModelo', form.ragFlowModelo.trim());
      }
      if (form.nicknameUrl?.trim()) {
        fd.append('nicknameUrl', form.nicknameUrl.trim());
      }
      fd.append('idRegional', activeRegionalId || '');
      fd.append('idArea', form.idArea);
      if (form.idMateria) {
        fd.append('idMateria', form.idMateria);
      }
      if (tipoId) {
        fd.append('idTipoAsistente', tipoId);
      }
      fd.append('publico', String(form.publico));
      fd.append('conPassword', String(!form.publico));
      if (!form.publico && form.passwordAsistente.trim()) {
        fd.append('passwordAsistente', form.passwordAsistente.trim());
      }
      archivosPdf.forEach((file) => fd.append('archivosPdf', file));
      const response = await AsistenteService.crearAsistente(fd, provider);

      if (response?.encolado) {
        setMessage(response.mensaje || 'creación del asistente AVE encolado, verifique el estado de creacion en la sección "Procesos".');
        setMessageType('success');
        setTimeout(() => navigate('/procesos', { replace: true }), 1200);
        return;
      }

      setMessage(`Asistente ${providerLabel} creado correctamente.`);
      setMessageType('success');
      setTimeout(() => navigate('/admin-dashboard', { replace: true, state: { tab: adminAssistantsTab } }), 900);
    } catch (error) {
      setMessage(error.message || 'No pudimos crear el asistente.');
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="new-dashboard admin-mode">
      <aside className="dashboard-sidebar">
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <i className="fas fa-university"></i>
            <span>Admin</span>
          </div>
        </div>
        <nav className="sidebar-nav">
          <AdminRoleMenuOptions
            user={user}
            isGptClassicEnabled={isGptClassicEnabled}
            isOpenAIAssistantEnabled={isOpenAIAssistantEnabled}
            isRagFlowAssistantEnabled={isRagFlowAssistantEnabled}
            activeItemId={adminAssistantsTab}
            onSelect={handleAdminMenuSelect}
          />
        </nav>
        <div className="sidebar-footer">
          <ActiveRegionalBadge />
          <div className="sidebar-user">
            <div className="user-avatar">
              <i className="fas fa-user-shield"></i>
            </div>
            <div className="user-info">
              <span className="user-name">{user?.displayName || user?.name || user?.email || 'Administrador'}</span>
              <span className="user-role">Admin</span>
            </div>
          </div>
        </div>
      </aside>

      <main className="dashboard-main">
        <header className="dashboard-header">
          <div className="header-left">
            <h1 className="page-title">Crear asistente {providerLabel}</h1>
            <p className="page-subtitle">Asigna un asistente {providerLabel} al regional {activeRegionalName}. La materia es obligatoria para grado y electiva, y opcional para institucional.</p>
          </div>
          <div className="header-right">
            <ActiveRegionalBadge />
            <UserAccountMenu />
          </div>
        </header>

        <div className="dashboard-content" style={{ padding: '2rem' }}>
          <div className="content-card">
            <div className="card-header">
              <div className="card-title">
                <i className="fas fa-robot" />
                <span>Datos del asistente {providerLabel}</span>
              </div>
            </div>
            <div className="card-content">
              <p style={{ marginTop: 0, color: '#475569' }}>
                Completa la información. El área es obligatoria; la materia sólo puede quedar vacía si el asistente es institucional.
              </p>
              <form onSubmit={handleSubmit} className="prof-create-form">
                <div className="assistant-form-grid">
                  <div className="assistant-form-panel">
                <div className="form-grid">
                  <div className="form-field">
                    <label>Nombre</label>
                    <input
                      type="text"
                      value={form.nombre}
                      onChange={(e) => handleChange('nombre', e.target.value)}
                      placeholder="Asistente X"
                    />
                  </div>
                  <div className="form-field">
                    <label>Nickname URL</label>
                    <input
                      type="text"
                      value={form.nicknameUrl}
                      onChange={(e) => handleNicknameChange(e.target.value)}
                      placeholder="ej: algebra-lineal-1"
                    />
                  </div>
	                  <div className="form-field">
	                    <label>Tipo de asistente</label>
	                    {canSelectTipo ? (
	                      <select
	                        value={form.idTipoAsistente || INSTITUCIONAL_ID}
                        onChange={(e) => handleChange('idTipoAsistente', e.target.value)}
                      >
                        <option value="">Selecciona un tipo</option>
                        {tiposAsistente.map((tipo) => (
                          <option key={tipo.id || tipo.Id} value={tipo.id || tipo.Id}>
                            {tipo.nombre || tipo.Nombre}
                          </option>
                        ))}
                      </select>
                    ) : (
	                      <input type="text" value="Institucional" disabled />
	                    )}
	                  </div>
	                  {isRagFlowProvider && (
	                    <div className="form-field">
	                      <label>Modelo AVE</label>
	                      <select
	                        value={form.ragFlowModelo}
	                        onChange={(e) => handleChange('ragFlowModelo', e.target.value)}
	                        disabled={loading || ragFlowModelsLoading}
	                        required
	                      >
	                        <option value="">
	                          {ragFlowModelsLoading ? 'Cargando modelos...' : 'Selecciona un modelo'}
	                        </option>
	                        {ragFlowModels.map((modelo) => (
	                          <option key={modelo.id} value={modelo.id}>
	                            {buildRagFlowModelLabel(modelo)}{modelo.esDefault ? ' (default)' : ''}
	                          </option>
	                        ))}
	                      </select>
	                      {ragFlowModelsError && (
	                        <small style={{ color: '#dc2626' }}>{ragFlowModelsError}</small>
	                      )}
	                      {selectedRagFlowModelConfig && (
	                        <div className="model-characteristics-card">
	                          <div className="model-characteristics-header">
	                            <i className="fas fa-circle-info"></i>
	                            <span>{buildRagFlowModelLabel(selectedRagFlowModelConfig)}</span>
	                          </div>
	                          {Array.isArray(selectedRagFlowModelConfig.caracteristicas) && selectedRagFlowModelConfig.caracteristicas.length > 0 && (
	                            <ul className="model-characteristics-list">
	                              {selectedRagFlowModelConfig.caracteristicas.map((caracteristica, index) => (
	                                <li key={`${selectedRagFlowModelConfig.id}-${index}`}>{caracteristica}</li>
	                              ))}
	                            </ul>
	                          )}
	                        </div>
	                      )}
	                    </div>
	                  )}
	                  <div className="form-field">
	                    <label>Área (obligatorio)</label>
	                    <select value={form.idArea} onChange={(e) => handleChange('idArea', e.target.value)}>
                      <option value="">Selecciona un área</option>
                      {areas.map((a) => (
                        <option key={a.id || a.Id} value={a.id || a.Id}>
                          {a.nombre || a.Nombre}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-field">
                    <label>{materiaEsObligatoria ? 'Materia (obligatoria)' : 'Materia (opcional)'}</label>
                    <select value={form.idMateria} onChange={(e) => handleChange('idMateria', e.target.value)}>
                      <option value="">{materiaEsObligatoria ? 'Selecciona una materia' : 'Sin materia'}</option>
                      {materias.map((m) => (
                        <option key={m.id || m.Id} value={m.id || m.Id}>
                          {m.nombre || m.Nombre}
                        </option>
                      ))}
                    </select>
                    {!materiaEsObligatoria && (
                      <small style={{ color: '#64748b' }}>
                        Si el asistente es institucional, podés dejar la materia vacía.
                      </small>
                    )}
                  </div>
                </div>

                <div className="form-field">
                  <label>Descripción</label>
                  <textarea
                    value={form.descripcion}
                    onChange={(e) => handleChange('descripcion', e.target.value)}
                    placeholder="Descripción opcional"
                    style={{ borderRadius: '10px', border: '1px solid var(--gray-200)', padding: '0.65rem 0.85rem', fontSize: '0.95rem', minHeight: '80px' }}
                  />
                </div>

                <div className="form-grid">
                  <div className="form-field">
                    <label>Visibilidad</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                      <input
                        type="checkbox"
                        checked={form.publico}
                        onChange={(e) => {
                          const nextValue = e.target.checked;
                          setForm((prev) => ({
                            ...prev,
                            publico: nextValue,
                            passwordAsistente: nextValue ? '' : prev.passwordAsistente
                          }));
                        }}
                      />
                      <span>Asistente público</span>
                    </div>
                  </div>
                </div>

                {!form.publico && (
                  <div className="form-field">
                    <label>Password</label>
                    <input
                      type="text"
                      value={form.passwordAsistente}
                      onChange={(e) => handleChange('passwordAsistente', e.target.value)}
                      placeholder="Ingresa el password (obligatorio)"
                    />
                  </div>
                )}

                <div className="form-section files-section" style={{ padding: 0, marginTop: '1rem' }}>
                  <div className="section-header" style={{ paddingLeft: 0 }}>
                    <div className="section-icon">
                      <i className="fas fa-file-pdf"></i>
                    </div>
                    <div className="section-text">
                      <h3 className="section-title" style={{ margin: 0 }}>Documentos de conocimiento</h3>
                      <p className="section-description" style={{ margin: 0 }}>Sube los PDFs igual que en la UI de profesor.</p>
                    </div>
                  </div>

                  <div
                    className={`file-dropzone ${isDragOver ? 'dragover' : ''} ${archivosPdf.length > 0 ? 'has-files' : ''}`}
                    onDragOver={(e) => {
                      e.preventDefault();
                      setIsDragOver(true);
                    }}
                    onDragLeave={() => setIsDragOver(false)}
                    onDrop={(e) => {
                      e.preventDefault();
                      setIsDragOver(false);
                      const files = Array.from(e.dataTransfer.files || []).filter((f) => f.type === 'application/pdf');
                      setArchivosPdf((prev) => [...prev, ...files].slice(0, 20));
                    }}
                  >
                    <div className="dropzone-border">
                      <div className="dropzone-content">
                        <div className="dropzone-icon">
                          <i className="fas fa-cloud-upload-alt"></i>
                        </div>
                        <div className="dropzone-text">
                          <h4>Arrastra y suelta tus archivos PDF aquí</h4>
                          <p>Hasta 20 archivos, máximo 10MB c/u</p>
                        </div>
                        <div className="dropzone-divider">
                          <span>o</span>
                        </div>
                        <label className="file-input-label">
                          <input
                            type="file"
                            multiple
                            accept=".pdf"
                            onChange={(e) => {
                              const files = Array.from(e.target.files || []).filter((f) => f.type === 'application/pdf');
                              setArchivosPdf((prev) => [...prev, ...files].slice(0, 20));
                            }}
                            disabled={loading}
                            style={{ display: 'none' }}
                          />
                          <span className="btn btn-upload">
                            <i className="fas fa-folder-open"></i>
                            Seleccionar Archivos PDF
                          </span>
                        </label>
                        <div className="upload-limits">
                          <div className="limit-item">
                            <i className="fas fa-file-alt"></i>
                            <span>Máximo 20 archivos</span>
                          </div>
                          <div className="limit-item">
                            <i className="fas fa-file-pdf"></i>
                            <span>Solo formato PDF</span>
                          </div>
                          <div className="limit-item">
                            <i className="fas fa-weight-hanging"></i>
                            <span>Hasta 10MB por archivo</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {archivosPdf.length > 0 && (
                    <div className="selected-files">
                      <h4>Archivos seleccionados ({archivosPdf.length}/20):</h4>
                      <div className="files-list">
                        {archivosPdf.map((file, index) => (
                          <div key={index} className="file-item">
                            <div className="file-info">
                              <i className="fas fa-file-pdf"></i>
                              <span className="file-name">{file.name}</span>
                              <span className="file-size">
                                {(file.size / 1024 / 1024).toFixed(2)} MB
                              </span>
                            </div>
                            <button
                              type="button"
                              className="remove-file-btn"
                              onClick={() => setArchivosPdf((prev) => prev.filter((_, i) => i !== index))}
                              disabled={loading}
                              title="Eliminar archivo"
                            >
                              <i className="fas fa-times"></i>
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

	                  {shouldShowExtraInstructions && (
	                    <div className="form-section prompt-settings-section" style={{ padding: 0, marginTop: '1.5rem' }}>
	                      <div className="section-header" style={{ paddingLeft: 0 }}>
	                        <div className="section-icon">
	                          <i className="fas fa-scroll"></i>
	                        </div>
	                        <div className="section-text">
	                          <h3 className="section-title" style={{ margin: 0 }}>
	                            {isRagFlowProvider ? 'Aclaraciones extra' : 'Instrucciones y aclaraciones'}
	                          </h3>
	                          <p className="section-description" style={{ margin: 0 }}>
	                            {isRagFlowProvider
	                              ? 'Estas aclaraciones se agregan al final del prompt generado para el asistente AVE.'
	                              : 'Completa la configuración textual del asistente después de cargar sus documentos.'}
	                          </p>
	                        </div>
	                      </div>
	
	                      {shouldShowPromptSettings && !materiaEsObligatoria && (
	                        <div className="prompt-extra-field">
	                          <label className="form-label">
                            <i className="fas fa-scroll"></i>
                            Instrucciones del asistente institucional *
                          </label>
                          <textarea
                            className="form-input enhanced"
                            rows="8"
                            value={form.instruccionesInstitucionales}
                            onChange={(e) => handleChange('instruccionesInstitucionales', e.target.value)}
                            placeholder="Describe el comportamiento y las reglas específicas que debe seguir este asistente institucional."
                            disabled={loading}
                          />
                          <div className="input-hint">
                            <i className="fas fa-info-circle"></i>
                            Este bloque se usará como cuerpo principal del prompt institucional, manteniendo la configuración operativa obligatoria antes del prompt extra.
                          </div>
                        </div>
                      )}

                      <div className="prompt-extra-field">
                        <label className="form-label">
                          <i className="fas fa-pen"></i>
                          Aclaraciones extra (opcional)
                        </label>
                        <textarea
                          className="form-input enhanced"
                          rows="6"
                          value={form.promptExtra}
                          onChange={(e) => handleChange('promptExtra', e.target.value)}
                          placeholder="Ej: prioriza ejercicios resueltos, responde en español neutro..."
                          disabled={loading}
                        />
                        <div className="input-hint">
                          <i className="fas fa-info-circle"></i>
                          Estas reglas se agregan al prompt base.
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {message && (
                  <div
                    className="modal-message"
                    style={{
                      marginTop: '0.5rem',
                      borderColor: messageType === 'success' ? '#bbf7d0' : messageType === 'error' ? '#fecaca' : 'var(--gray-200)',
                      background: messageType === 'success' ? '#dcfce7' : messageType === 'error' ? '#fee2e2' : '#f8fafc',
                      color: messageType === 'error' ? '#7f1d1d' : messageType === 'success' ? '#0f5132' : 'var(--gray-800)'
                    }}
                  >
                    {loading && <i className="fas fa-spinner fa-spin" style={{ marginRight: '0.35rem' }} />}
                    {message}
                  </div>
                )}

                  </div>

                  {shouldShowPromptSettings && !isInstitutionalAssistantType(selectedTipoAsistente || { id: form.idTipoAsistente || INSTITUCIONAL_ID }) && (
                    <aside className="assistant-prompt-panel">
                      <div className="prompt-panel-header">
                        <i className="fas fa-clipboard-list"></i>
                        <div>
                          <h3>Comportamiento del asistente</h3>
                          <p>Resumen del prompt base configurado.</p>
                        </div>
                      </div>
                      <ul className="prompt-features-list">
                        {PROMPT_FEATURES.map((item) => (
                          <li key={item}>
                            <i className="fas fa-check-circle"></i>
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </aside>
                  )}
                </div>

                <div className="form-actions">
                  <button type="button" className="btn btn-secondary" onClick={() => navigate('/admin-dashboard', { state: { tab: adminAssistantsTab } })}>
                    Cancelar
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={isSubmitDisabled}>
                    {loading ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-save" />}
                    <span>Crear asistente {providerLabel}</span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminCreateAsistente;
